/**
 * MarginComposition — the Group→Entity drill-through, on served cells. For each
 * additive P&L metric it shows the Kirkstall + Oracle values and their Group parent,
 * with the §5 sum check (Group = Kirkstall + Oracle) computed on the real cells. This
 * is the platform's reconciling decomposition, made visible — arithmetic on the served
 * trust envelope, never fabricated. Below entity there is no GL-line grain (Sage serves
 * account-group balances), so the panel says so rather than inventing a breakdown.
 */

import React from 'react'
import { Check, X } from 'lucide-react'
import { formatCellValue, EM_DASH } from '../components/kit/cellFormat'
import { COMPOSITION_SITES, siteReconcile } from '../viewmodels/margin'
import type { Cell } from '../types/metrics'

const ADDITIVE: { id: string; label: string }[] = [
  { id: 'revenue', label: 'Revenue' },
  { id: 'cogs', label: 'COGS' },
  { id: 'gross_profit', label: 'Gross Profit' },
  { id: 'opex', label: 'OPEX' },
  { id: 'ebitda', label: 'EBITDA' },
]

const HEAD = 'px-3.5 py-2.5 border-b border-line font-mono text-[10px] uppercase tracking-[.06em] text-mut2'
const CELL = 'px-3.5 py-2.5 border-b border-line2 text-right tabular-nums'

function val(cell: Cell | null): string {
  return cell && cell.value !== null ? formatCellValue(cell) : EM_DASH
}

function ReconcileChip({ ok }: { ok: boolean }): React.JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-[10px] ${ok ? 'text-good bg-good-bg' : 'text-bad bg-bad-bg'}`}
      title={ok ? 'Group = Kirkstall + Oracle (served cells)' : 'served cells do not sum to the Group value'}
    >
      {ok ? <Check size={11} /> : <X size={11} />}
      {ok ? 'reconciles' : 'MISMATCH'}
    </span>
  )
}

/** One metric: its entity parts, the Group parent, and the sum check on the served cells. */
function Row({ lookup, id, label, period }: {
  lookup: Map<string, Cell>
  id: string
  label: string
  period: string
}): React.JSX.Element {
  const r = siteReconcile(lookup, id, period)
  return (
    <tr className="cx-row">
      <td className="px-3.5 py-2.5 border-b border-line2 font-medium text-ink">{label}</td>
      {r.parts.map((p) => (
        <td key={p.site} className={CELL}>{val(p.cell)}</td>
      ))}
      <td className={`${CELL} font-semibold text-ink`}>{val(r.group)}</td>
      <td className="px-3.5 py-2.5 border-b border-line2 text-right"><ReconcileChip ok={r.reconciles} /></td>
    </tr>
  )
}

export default function MarginComposition({ lookup, period, label }: {
  lookup: Map<string, Cell>
  /** The key the cells are held at — a Sage month, or the window aggregate's pseudo-period. */
  period: string
  /** What that key COVERS, for the header. The window aggregate is keyed at a name, not a
   * date, so the column has to be told the span it reports. */
  label?: string
}): React.JSX.Element {
  return (
    <table className="w-full min-w-[560px] border-collapse text-xs">
      <thead>
        <tr>
          <th className={`${HEAD} text-left`}>Metric · {label ?? period}</th>
          {COMPOSITION_SITES.map((s) => (
            <th key={s} className={`${HEAD} text-right`}>{s}</th>
          ))}
          <th className={`${HEAD} text-right`}>Σ Group</th>
          <th className={`${HEAD} text-right`}>Sum check</th>
        </tr>
      </thead>
      <tbody>
        {ADDITIVE.map((metric) => (
          <Row key={metric.id} lookup={lookup} id={metric.id} label={metric.label} period={period} />
        ))}
      </tbody>
    </table>
  )
}
